import { useState, useEffect, useId, MouseEvent, useCallback } from 'react'
import { useClickOutside } from '@/core/hooks/useClickOutside'
import { useCustomTheme } from '@/core/theme'

let activePopupId: string | null = null
const listeners = new Set<(id: string | null) => void>()

const setActivePopup = (id: string | null) => {
    activePopupId = id
    listeners.forEach((listener) => listener(id))
}

export const usePopConfirm = (
    onCancel?: () => void,
    onConfirm?: () => void
) => {
    const theme: any = useCustomTheme()
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
    const [open, setOpen] = useState(false)
    const [currentActiveId, setCurrentActiveId] = useState(activePopupId)
    const instanceId = useId()

    const canBeOpen = open && Boolean(anchorEl)
    const id = canBeOpen ? instanceId : ''

    useEffect(() => {
        const listener = (id: string | null) => {
            setCurrentActiveId(id)
        }
        listeners.add(listener)

        return () => {
            listeners.delete(listener)
        }
    }, [])

    useEffect(() => {
        if (currentActiveId && currentActiveId !== instanceId && open) {
            setOpen(false)
            setAnchorEl(null)
        }
    }, [currentActiveId, open, instanceId])

    const handleClick = (event: MouseEvent<HTMLElement>) => {
        const willOpen = !open

        if (willOpen) {
            setActivePopup(instanceId)
            setAnchorEl(event.currentTarget)
            setOpen(true)
        } else {
            setActivePopup(null)
            setOpen(false)
            setAnchorEl(null)
        }
    }

    const handleCancel = () => {
        onCancel?.()
        setActivePopup(null)
        setOpen(false)
        setAnchorEl(null)
    }

    const handleConfirm = () => {
        onConfirm?.()
        setActivePopup(null)
        setOpen(false)
        setAnchorEl(null)
    }

    const handleClickOutside = useCallback(() => {
        if (open) {
            setActivePopup(null)
            setOpen(false)
            setAnchorEl(null)
        }
    }, [open])

    const popperRef = useClickOutside<HTMLDivElement>(handleClickOutside, {
        enabled: open,
    })

    const popperStyles = {
        maxWidth: '260px',
        borderRadius: '8px',
        padding: '18px 14px',
        boxShadow: '0 8px 16px rgba(0, 0, 0, 0.15), 0 0 1px rgba(0, 0, 0, 0.1)',
        border: `1px solid ${
            theme.colors?.semantic?.separator || theme.colors.greys[200]
        }`,
        backgroundColor:
            theme.colors?.semantic?.backgroundSecondary ||
            theme.colors.greys[0],
        zIndex: 9999,
    }

    const buttonColors = {
        cancel: {
            text: theme.colors.greys[700],
            background: theme.colors.greys[100],
            hover: theme.colors.greys[200],
        },
        confirm: {
            text: theme.colors.greys[0],
            background: theme.colors.primary[400],
            hover: theme.colors.primary[500],
        },
    }

    return {
        open,
        anchorEl,
        id,
        popperRef,
        handleClick,
        handleCancel,
        handleConfirm,
        popperStyles,
        buttonColors,
    }
}
